import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { formationsRequirements } from '../data/formationsRequirements';
import { cn } from '../lib/utils';
import { useApp } from '../contexts/AppContext';
import { Counter } from './Counter';

const Chip = ({ color, soft, children, icon: Icon, dark }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black uppercase tracking-wider"
    style={{ background: dark ? `${color}25` : soft, color }}
  >
    {Icon && <Icon className="w-3.5 h-3.5" />}
    {children}
  </span>
);

export const FormationRequirements = ({ formationKey }) => {
  const [open, setOpen] = useState(true);
  const { dark } = useApp();
  const data = formationsRequirements[formationKey];
  if (!data) return null;

  const Icon = Icons[data.icon] || Icons.Bike;
  // Helper: en dark mode, on utilise la couleur accent avec opacité faible au lieu du pastel clair
  const softBg = (opacity = '20') => dark ? `${data.color}${opacity}` : data.soft;
  const softBorder = dark ? `${data.color}60` : `${data.color}40`;

  return (
    <div
      className="mc-card overflow-hidden"
      data-testid={`formation-requirements-${formationKey.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start gap-4 p-5 sm:p-6 text-left hover:bg-[hsl(var(--panel))] transition-colors"
        data-testid="formation-req-toggle"
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: softBg('25'), color: data.color }}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Chip color={data.color} soft={data.soft} dark={dark}>Pré-requis &amp; conditions</Chip>
          </div>
          <h3 className="font-black text-lg sm:text-xl tracking-tight leading-tight">{formationKey}</h3>
          <p className="text-sm text-muted-foreground mt-1">{data.tagline}</p>
        </div>
        <Icons.ChevronDown
          className={cn('w-5 h-5 text-muted-foreground transition-transform flex-shrink-0 mt-1', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="px-5 pb-5 sm:px-6 sm:pb-6 space-y-5">
          {/* Ligne 1 — Véhicules autorisés + âge minimum */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-[hsl(var(--panel))] p-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                <Icons.Wrench className="w-3 h-3" />
                <span>Véhicules autorisés</span>
              </div>
              <div className="space-y-2">
                {data.vehicules.map((v, i) => {
                  const VIcon = Icons[v.iconKey] || Icons.Bike;
                  return (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: softBg('20'), color: data.color }}>
                        <VIcon className="w-4 h-4" />
                      </span>
                      <div className="flex-1">
                        <div className="font-bold text-sm">{v.label}</div>
                        <div className="text-xs text-muted-foreground">{v.detail}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className="rounded-2xl p-4 border-2"
              style={{ background: softBg('20'), borderColor: softBorder }}
            >
              <div className="text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-1.5" style={{ color: data.color }}>
                <Icons.Cake className="w-3 h-3" />
                <span>Âge minimum</span>
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-black" style={{ color: data.color }}>
                  <Counter key={formationKey} value={data.age.min} duration={1000} />
                </span>
                <span className="text-lg font-bold" style={{ color: data.color }}>ans</span>
              </div>
              <p className="text-xs text-foreground/80">{data.age.text}</p>
            </div>
          </div>

          {/* Ligne 2 — Pré-requis obligatoires */}
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
              <Icons.ClipboardList className="w-3 h-3" />
              <span>Pré-requis obligatoires avant intégration</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.prerequis.map((p, i) => {
                const PIcon = Icons[p.icon] || Icons.CheckCircle2;
                return (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-border bg-[hsl(var(--panel))] p-3">
                    <span
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: softBg('25'), color: data.color }}
                    >
                      <PIcon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm">{p.label}</div>
                      <div className="text-xs text-muted-foreground">{p.detail}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ligne 3 — Durée de formation */}
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                <Icons.Timer className="w-3 h-3" />
                <span>Durée de formation</span>
              </div>
              <div className="font-black text-lg" style={{ color: data.color }}>{data.duree.total}</div>
            </div>
            <p className="text-xs text-muted-foreground mb-3">{data.duree.detail}</p>
            <div className="grid sm:grid-cols-3 gap-2">
              {data.duree.breakdown.map((b, i) => (
                <div key={i} className="rounded-xl p-3 text-center" style={{ background: softBg('18') }}>
                  <div className="font-black text-xl" style={{ color: data.color }}>{b.h}</div>
                  <div className="text-xs font-semibold text-foreground/80 mt-0.5">{b.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Ligne 4 — Examen */}
          <div
            className="rounded-2xl border-2 p-4"
            style={{
              background: data.examen.hasExam
                ? (dark ? 'rgba(245,158,11,0.15)' : '#fff7e8')
                : (dark ? 'rgba(24,121,78,0.15)' : 'hsl(var(--success-soft))'),
              borderColor: data.examen.hasExam ? (dark ? 'rgba(245,158,11,0.4)' : '#f5dfad') : (dark ? 'rgba(24,121,78,0.4)' : '#c8edd6')
            }}
          >
            <div className="flex items-start gap-3">
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: data.examen.hasExam ? '#9a6700' : '#18794e',
                  color: 'white'
                }}
              >
                {data.examen.hasExam
                  ? <Icons.ClipboardCheck className="w-5 h-5" />
                  : <Icons.CheckCircle2 className="w-5 h-5" />
                }
              </span>
              <div className="flex-1">
                <div className="text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-1.5"
                  style={{ color: data.examen.hasExam ? '#9a6700' : '#18794e' }}
                >
                  {data.examen.hasExam
                    ? <Icons.FileCheck className="w-3 h-3" />
                    : <Icons.CheckCircle2 className="w-3 h-3" />
                  }
                  <span>{data.examen.hasExam ? 'Examen final requis' : "Pas d'examen"}</span>
                </div>
                <p className="text-sm text-foreground/85">{data.examen.text}</p>
                {data.examen.parts && (
                  <ul className="mt-2 space-y-1">
                    {data.examen.parts.map((p, i) => (
                      <li key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
                        <Icons.ChevronRight className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#9a6700' }} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Ligne 5 — Autorisations + conseils */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                <Icons.CheckCircle2 className="w-3 h-3" />
                <span>Ce que tu pourras faire</span>
              </div>
              <ul className="space-y-2">
                {data.autorisations.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Icons.CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: data.color }} />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="rounded-2xl border-2 p-4"
              style={{ background: softBg('18'), borderColor: softBorder }}
            >
              <div className="text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-1.5" style={{ color: data.color }}>
                <Icons.Lightbulb className="w-3 h-3" />
                <span>Conseils avant de te lancer</span>
              </div>
              <ul className="space-y-2">
                {data.conseilsEleve.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Icons.Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: data.color }} />
                    <span className="text-foreground/85">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Ligne 6 — Coût indicatif formation */}
          <div className="flex items-center gap-3 rounded-xl border border-border bg-[hsl(var(--panel))] p-3.5">
            <Icons.Euro className="w-5 h-5 flex-shrink-0" style={{ color: data.color }} />
            <div className="flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Coût indicatif de la formation</div>
              <div className="text-sm font-bold">{data.coutIndicatif}</div>
            </div>
            <span className="text-[10px] text-muted-foreground font-semibold italic">selon ton école</span>
          </div>

          {/* Ligne 7 — Assurances */}
          {data.assurance && (
            <div
              className="rounded-2xl border-2 p-4"
              style={{
                background: dark ? 'rgba(29,78,216,0.15)' : '#eef5ff',
                borderColor: dark ? 'rgba(29,78,216,0.4)' : 'rgba(29,78,216,0.2)'
              }}
              data-testid="formation-req-assurance"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="w-9 h-9 rounded-xl bg-[#1d4ed8] text-white flex items-center justify-center flex-shrink-0">
                  <Icons.ShieldCheck className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5" style={{ color: dark ? '#93bbff' : '#1d4ed8' }}>
                    <Icons.ShieldCheck className="w-3 h-3" />
                    <span>Assurances</span>
                  </div>
                  <div className="font-black text-sm">Ce qui est obligatoire, conseillé et utile à savoir.</div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="rounded-xl bg-card border border-[#1d4ed8]/20 dark:border-[#1d4ed8]/40 p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Icons.AlertCircle className="w-4 h-4 text-[#b42318]" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#b42318]">Obligatoire</span>
                  </div>
                  <ul className="space-y-1.5">
                    {data.assurance.obligatoire.map((o, i) => (
                      <li key={i} className="text-xs text-foreground/85 flex items-start gap-1.5">
                        <Icons.Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#b42318]" />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl bg-card border border-[#1d4ed8]/20 dark:border-[#1d4ed8]/40 p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Icons.Star className="w-4 h-4 text-[#18794e]" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#18794e]">Conseillé</span>
                  </div>
                  <ul className="space-y-1.5">
                    {data.assurance.conseillee.map((c, i) => (
                      <li key={i} className="text-xs text-foreground/85 flex items-start gap-1.5">
                        <Icons.Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#18794e]" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div className="flex items-center gap-2.5 rounded-xl bg-card border border-[#1d4ed8]/20 dark:border-[#1d4ed8]/40 p-3">
                  <Icons.Euro className="w-4 h-4 flex-shrink-0" style={{ color: dark ? '#93bbff' : '#1d4ed8' }} />
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: dark ? '#93bbff' : '#1d4ed8' }}>Coût annuel</div>
                    <div className="text-xs font-bold">{data.assurance.coutAnnuel}</div>
                  </div>
                </div>
                <div className="rounded-xl bg-card border border-[#1d4ed8]/20 dark:border-[#1d4ed8]/40 p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icons.Lightbulb className="w-4 h-4 text-[#f59e0b]" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#f59e0b]">Astuces pros</span>
                  </div>
                  <ul className="space-y-1">
                    {data.assurance.astuces.map((a, i) => (
                      <li key={i} className="text-[11px] text-foreground/85 flex items-start gap-1.5">
                        <span className="flex-shrink-0 mt-0.5">•</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
